if (!customElements.get('mobile-sticky-atc-sht')) {
  customElements.define(
    'mobile-sticky-atc-sht',
    class MobileStickyAtcSht extends HTMLElement {
      connectedCallback() {
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        this.displayMode = this.dataset.displayMode || 'auto_non_pc';
        this.maxWidth = Number(this.dataset.mobileMaxWidth || 990);
        this.enableThemeEditorPreview = this.dataset.enableThemeEditorPreview === 'true';
        this.triggerMode = this.dataset.triggerMode || 'scroll_px';
        this.showAfterPx = Number(this.dataset.showAfterPx || 700);
        this.purchaseAreaSelectors = this.dataset.purchaseAreaSelectors || '[data-mvps-root], .product-form__buttons';
        this.addButtonSelectors = this.dataset.addButtonSelectors || '[data-mvps-add-to-cart], .product-form__submit, button[name="add"]';
        this.widthQuery = window.matchMedia(`(max-width: ${this.maxWidth}px)`);
        this.pointerQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
        this.form = this.getProductForm();
        this.sourceButton = this.getSourceButton();
        this.purchaseAreas = this.getPurchaseAreas();
        this.stickyButton = this.querySelector('[data-msatc-add]');
        this.scrollButton = this.querySelector('[data-msatc-scroll]');
        this.handleCartDrawerOpen = () => this.updateVisibility();
        this.handleCartDrawerClose = () => this.updateVisibility();

        if (!this.stickyButton) return;

        this.syncButtonState();
        this.bindEvents();
        this.setupTrigger();
      }

      getProductForm() {
        return (
          document.getElementById(this.dataset.productFormId) ||
          document.querySelector('form[data-type="add-to-cart-form"]') ||
          document.querySelector('form[action*="/cart/add"]')
        );
      }

      querySelectorList(selectorList) {
        return selectorList
          .split(',')
          .map((selector) => selector.trim())
          .filter(Boolean)
          .flatMap((selector) => {
            try {
              return Array.from(document.querySelectorAll(selector));
            } catch (error) {
              console.warn('mobile-sticky-atc-sht: invalid selector', selector);
              return [];
            }
          });
      }

      getPurchaseAreas() {
        const customTarget = this.dataset.targetSelector?.trim();
        if (customTarget) return this.querySelectorList(customTarget);

        const areas = this.querySelectorList(this.purchaseAreaSelectors);
        return areas.length ? areas : [this.sourceButton].filter(Boolean);
      }

      getSourceButton() {
        const preferredButtons = this.querySelectorList(this.addButtonSelectors);
        return preferredButtons[0] || document.getElementById(this.dataset.submitButtonId) || this.form?.querySelector('[type="submit"], button[name="add"]');
      }

      bindEvents() {
        this.stickyButton.addEventListener('click', () => this.submitOriginalForm());
        this.scrollButton?.addEventListener('click', () => this.scrollToSourceButton());

        if (this.form) {
          this.form.addEventListener('submit', () => {
            this.stickyButton.classList.add('is-loading');
            window.setTimeout(() => this.stickyButton.classList.remove('is-loading'), 1800);
          });
        }

        if (this.sourceButton) {
          this.sourceButtonObserver = new MutationObserver(() => this.syncButtonState());
          this.sourceButtonObserver.observe(this.sourceButton, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['disabled'],
          });
        }

        this.widthQuery.addEventListener('change', () => this.updateVisibility());
        this.pointerQuery.addEventListener('change', () => this.updateVisibility());
        document.addEventListener('cart-drawer:open', this.handleCartDrawerOpen);
        document.addEventListener('cart-drawer:close', this.handleCartDrawerClose);
      }

      setupTrigger() {
        if (this.triggerMode === 'button_visibility') {
          if (this.purchaseAreas.length) {
            this.observePurchaseAreas();
          } else {
            this.triggerReached = window.scrollY >= this.showAfterPx;
            window.addEventListener('scroll', () => {
              this.triggerReached = window.scrollY >= this.showAfterPx;
              this.updateVisibility();
            }, { passive: true });
            this.updateVisibility();
          }
          return;
        }

        this.onScroll = () => {
          this.triggerReached = window.scrollY >= this.showAfterPx;
          this.updateVisibility();
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      observeSourceButton() {
        this.observePurchaseAreas();
      }

      observePurchaseAreas() {
        this.visiblePurchaseAreas = new WeakSet();
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.visiblePurchaseAreas.add(entry.target);
              } else {
                this.visiblePurchaseAreas.delete(entry.target);
              }
            });
            this.sourceButtonVisible = this.purchaseAreas.some((area) => this.visiblePurchaseAreas.has(area));
            this.updateVisibility();
          },
          {
            root: null,
            threshold: 0.12,
          }
        );

        this.purchaseAreas.forEach((area) => this.observer.observe(area));
      }

      updateVisibility() {
        const enabled = this.isEnabledForDevice();
        const triggerActive = this.triggerMode === 'button_visibility' ? !this.sourceButtonVisible : this.triggerReached;
        const canDisplay = enabled && !this.isCartDrawerOpen();
        const shouldShow = canDisplay && triggerActive;
        this.hidden = !shouldShow;
        this.classList.toggle('is-enabled', shouldShow);
        this.classList.toggle('is-visible', shouldShow);
      }

      isCartDrawerOpen() {
        return document.body.classList.contains('cart-drawer-open') || Boolean(document.querySelector('cart-drawer.active'));
      }

      isEnabledForDevice() {
        if (this.enableThemeEditorPreview && window.Shopify?.designMode && this.widthQuery.matches) return true;
        if (this.displayMode === 'always') return true;
        if (this.displayMode === 'screen_width') return this.widthQuery.matches;

        const userAgent = navigator.userAgent || '';
        const isMobileUserAgent = /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(userAgent);
        return this.pointerQuery.matches || isMobileUserAgent || this.widthQuery.matches;
      }

      syncButtonState() {
        if (!this.sourceButton) return;
        this.stickyButton.disabled = this.sourceButton.disabled;

        const sourceText = this.sourceButton.querySelector('span')?.textContent?.trim();
        const stickyText = this.querySelector('[data-msatc-button-text]');
        if (sourceText && stickyText) stickyText.textContent = sourceText;
      }

      submitOriginalForm() {
        if (this.stickyButton.disabled) return;
        this.stickyButton.classList.add('is-loading');

        this.form = this.form || this.getProductForm();
        this.sourceButton = this.sourceButton || this.getSourceButton();

        if (this.sourceButton?.matches?.('[data-mvps-add-to-cart]')) {
          this.sourceButton.click();
        } else if (this.form && typeof this.form.requestSubmit === 'function') {
          if (this.sourceButton) {
            this.form.requestSubmit(this.sourceButton);
          } else {
            this.form.requestSubmit();
          }
        } else if (this.sourceButton) {
          this.sourceButton.click();
        } else {
          this.stickyButton.classList.remove('is-loading');
        }
      }

      scrollToSourceButton() {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.sourceButtonObserver?.disconnect();
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('cart-drawer:open', this.handleCartDrawerOpen);
        document.removeEventListener('cart-drawer:close', this.handleCartDrawerClose);
      }
    }
  );
}
